import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAdminAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Users } from "lucide-react";

export default function AdminSegnalatori() {
  const nav = useNavigate();
  const { user } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === null) nav("/admin/login");
    else if (user && user.role !== "admin") {
      toast.error("Area riservata all'amministratore");
      nav("/");
    }
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/segnalatori");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.error("Il nome del segnalatore è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/segnalatori", form);
      toast.success("Segnalatore aggiunto");
      setForm({ nome: "", email: "", telefono: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, nome) => {
    if (!window.confirm(`Eliminare il segnalatore "${nome}"? Verrà rimosso anche da tutte le pratiche.`)) return;
    try {
      await api.delete(`/admin/segnalatori/${id}`);
      toast.success("Segnalatore eliminato");
      setItems((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (user === undefined) return <div className="max-w-4xl mx-auto p-10 text-ink-muted">Caricamento...</div>;
  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand">
        <ArrowLeft className="w-4 h-4" /> Torna alla dashboard
      </Link>
      <div className="mt-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded bg-brand/10 text-brand flex items-center justify-center">
          <Users className="w-5 h-5" />
        </div>
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">Anagrafica segnalatori</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-ink">Segnalatori</h1>
        </div>
      </div>
      <p className="mt-3 text-sm text-ink-muted max-w-2xl">
        Gestisci i nominativi che possono essere collegati alle pratiche. Quando un segnalatore viene attivato su una pratica,
        vengono decurtati <span className="text-ink font-semibold">500€ fissi</span> dalla quota commerciale di Tommaso e riaccreditati al segnalatore.
      </p>

      {/* Form crea */}
      <form onSubmit={onCreate} className="mt-8 bg-white border border-line rounded-lg shadow-card p-5 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="seg-form">
        <input
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
          placeholder="Nome segnalatore *"
          data-testid="seg-input-nome"
          className="h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm md:col-span-2"
        />
        <input
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email (opzionale)"
          data-testid="seg-input-email"
          className="h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
        />
        <input
          value={form.telefono}
          onChange={(e) => setForm({ ...form, telefono: e.target.value })}
          placeholder="Telefono (opzionale)"
          data-testid="seg-input-tel"
          className="h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
        />
        <textarea
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="Note (opzionale)"
          data-testid="seg-input-note"
          className="min-h-[44px] px-3 py-2 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm md:col-span-3"
          rows={1}
        />
        <button
          type="submit"
          disabled={saving}
          data-testid="seg-submit"
          className="h-11 bg-brand text-white rounded font-display uppercase tracking-widest text-sm hover:bg-brand-dark inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Plus className="w-4 h-4" /> Aggiungi
        </button>
      </form>

      {/* Lista */}
      <div className="mt-6 bg-white border border-line rounded-lg shadow-card overflow-hidden" data-testid="seg-list">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">Nessun segnalatore ancora inserito.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bg-dark text-white">
              <tr>
                <th className="text-left px-4 py-3 font-display text-[11px] tracking-widest uppercase">Nome</th>
                <th className="text-left px-4 py-3 font-display text-[11px] tracking-widest uppercase">Contatti</th>
                <th className="text-left px-4 py-3 font-display text-[11px] tracking-widest uppercase">Note</th>
                <th className="text-right px-4 py-3 font-display text-[11px] tracking-widest uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((s) => (
                <tr key={s.id} className="hover:bg-line/30" data-testid={`seg-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-ink">{s.nome}</td>
                  <td className="px-4 py-3 text-ink-muted">
                    {s.email && <div className="text-ink">{s.email}</div>}
                    {s.telefono && <div className="text-xs">{s.telefono}</div>}
                    {!s.email && !s.telefono && <span>—</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-muted max-w-xs truncate" title={s.note || ""}>{s.note || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onDelete(s.id, s.nome)}
                      data-testid={`seg-delete-${s.id}`}
                      className="p-2 border border-line rounded hover:bg-red-600 hover:text-white hover:border-red-600"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
